// src/utils/evidenceHar.js
//
// Evidence HARs for the Launch workspace (WP1, docs/ticket-validation-workflow-spec.md §4):
// a HAR holding exactly the requests a criterion's checks graded.
//
// Invariant: the requestIds come ONLY from the proxy's per-check `gradedIds` /
// `failedIds` (POST /proxy/checks/run with gradedIds:true). Nothing here
// re-derives which rows a check selected; a second copy of that matcher would
// drift, and the HAR would then disagree with the verdict it is meant to prove.
//
// The id lists travel INSIDE the saved HAR (`log._inspectra.evidence.criteria`),
// not on the launch record, so a launch HAR saved today can still be sliced
// per criterion weeks later, after the live rows (and their bodies) are gone.
//
// Pure functions except persistRunEvidence (IO injected) and downloadHarFile (DOM).

import { buildHarFromRequests } from './buildHar';
import { criterionResults, criterionRollup } from './launchRollup';

// backend/index.js SaveHarSchema: labels max 40 chars each, title max 200 —
// one over-long label rejects the whole save.
const LABEL_MAX = 40;
const TITLE_MAX = 200;

function uniq(list) {
  return [...new Set(list)];
}

// ---- launch-redesign T24 (D10, D23): per-row evidence (values[], branchId, naReason, urlRole) in the HAR.
// ID-class masking is by field NAME, twin of proxy-server/conformance/idClass.js + evidenceValues.js
// (a test pins the name list). The proxy already masks; this re-masks so a result from an older proxy or a
// hand-built one never writes a clear rdid / ppid / eo_id into a saved HAR.
export const ID_CLASS_NAMES = [
  'rdid', 'rd_?id', 'ppid', 'eo_?id', 'idfa', 'ifa', 'gaid', 'aaid', 'idfv', 'vcid', 'uid', 'did',
  'buyeruid', 'adid', 'advertising_?id', 'android_?id', 'device_?id', 'user_?id',
];
const ALT = ID_CLASS_NAMES.join('|');
const ID_CLASS_RE = new RegExp(`(?:^|[^a-z])(${ALT})(?:[^a-z]|$)`, 'i');
const Q = '(?:["\']|%(?:25)?(?:22|27))';
const SEP = '(?:[=:]|%(?:25)?(?:3d|3a))';
// Value ends at & " ' whitespace , ; } ( ) < > [ ] or %26/%22/%27 (and double-encoded). '/' does NOT end it. Twin of
// proxy-server/conformance/idClass.js VAL; `ppid=abc(1)` leaves `(1)` clear (known low leak).
const VAL = '((?:(?!%(?:25)?(?:26|22|27))[^&"\'\\s,;}()<>\\[\\]])+)';
export const ID_CLASS_PAIR_SRC = `((?:^|[^a-z])(?:${ALT})(?:_expanded)?[0-9]*${Q}?\\s*${SEP}\\s*${Q}?)${VAL}`;
const ID_CLASS_PAIR_RE = new RegExp(ID_CLASS_PAIR_SRC, 'gi');
/** Mask every ID-class name=value pair inside a string (twin of proxy maskIdText). */
export function maskIdText(text) {
  if (typeof text !== 'string' || !text) return text;
  return text.replace(ID_CLASS_PAIR_RE, (_m, head, val) => `${head}${maskIdValue(val)}`);
}
const VALUE_CAP = 200; // EVIDENCE_VALUE_CAP
const ROWS_PER_CRITERION_CAP = 200;
const VALUES_PER_ROW_CAP = 20;

function maskIdValue(v) {
  const s = String(v == null ? '' : v);
  if (s.length > 12) return `${s.slice(0, 8)}…${s.slice(-4)}`;
  return s === '' ? '' : '[masked]';
}

function safeEvidenceValue(raw, param) {
  const s = typeof raw === 'string' ? raw : String(raw == null ? '' : raw);
  const masked = typeof param === 'string' && ID_CLASS_RE.test(param) ? maskIdValue(s) : s.replace(ID_CLASS_PAIR_RE, (_m, head, val) => `${head}${maskIdValue(val)}`);
  return masked.length > VALUE_CAP ? `${masked.slice(0, VALUE_CAP - 1)}…` : masked;
}

function evidenceValues(values) {
  if (!Array.isArray(values)) return undefined;
  const out = [];
  for (const v of values) {
    if (out.length >= VALUES_PER_ROW_CAP) break;
    if (!v || typeof v !== 'object') continue;
    const e = { ...v };
    if (typeof e.value === 'string') e.value = safeEvidenceValue(e.value, e.param);
    out.push(e);
  }
  return out;
}

/** Evidence rows of one check result worth keeping in the HAR (those with values / unit / n/a cause). */
function evidenceRows(checkId, rows) {
  const out = [];
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r || typeof r !== 'object') continue;
    if (!Array.isArray(r.values) && !r.branchId && !r.naReason) continue;
    const e = { checkId, requestId: r.requestId ?? null };
    if (r.branchId) e.branchId = r.branchId;
    if (r.naReason) e.naReason = r.naReason;
    if (r.urlRole) e.urlRole = r.urlRole;
    if (typeof r.ok === 'boolean') e.ok = r.ok;
    const values = evidenceValues(r.values);
    if (values) e.values = values;
    out.push(e);
  }
  return out;
}

/**
 * criterionEvidence(criteria, resultsByCheckId)
 *   -> { [criterionId]: { gradedIds, failedIds, gradedIdsUnavailable?, failedIdsUnavailable?, gradedIdsTruncated? } }
 * Unions each criterion's mapped checks' id lists. A criterion with no mapped
 * checks (manual/unmapped) is omitted: it has no traffic evidence. A result
 * that carries no id list at all (older proxy, or the check never ran) marks
 * the criterion unavailable rather than reading as "graded nothing".
 */
export function criterionEvidence(criteria, resultsByCheckId) {
  const out = {};
  for (const c of Array.isArray(criteria) ? criteria : []) {
    if (!c?.id) continue;
    const checks = criterionResults(c, resultsByCheckId || {});
    if (checks.length === 0) continue;
    const graded = [];
    const failed = [];
    const entry = {};
    const rows = [];
    for (const { result } of checks) {
      if (result) rows.push(...evidenceRows(result.checkId, result.rows));
      if (!result || !Array.isArray(result.gradedIds)) { entry.gradedIdsUnavailable = true; continue; }
      graded.push(...result.gradedIds);
      if (Array.isArray(result.failedIds)) failed.push(...result.failedIds);
      if (result.gradedIdsUnavailable) entry.gradedIdsUnavailable = true;
      if (result.failedIdsUnavailable) entry.failedIdsUnavailable = true;
      if (result.gradedIdsTruncated) entry.gradedIdsTruncated = true;
    }
    if (rows.length) {
      entry.rows = rows.slice(0, ROWS_PER_CRITERION_CAP);
      if (rows.length > ROWS_PER_CRITERION_CAP) entry.rowsTruncated = true;
    }
    out[c.id] = { gradedIds: uniq(graded), failedIds: uniq(failed), ...entry };
  }
  return out;
}

/** Every graded id across all criteria, deduped, in first-seen order. */
export function allGradedIds(evidence) {
  return uniq(Object.values(evidence || {}).flatMap((e) => e?.gradedIds || []));
}

/**
 * buildEvidenceHar({ evidence, rowsById, meta }) -> { har, missingIds }
 * HAR of the union of all criteria's graded rows. `rowsById` maps requestId ->
 * captured row (the dashboard's in-memory rows). Ids the browser no longer
 * holds are listed in `missingIds` and in the HAR itself, so a short HAR says
 * it is short instead of passing for complete.
 */
export function buildEvidenceHar({ evidence, rowsById, meta = {} }) {
  const ids = allGradedIds(evidence);
  const rows = [];
  const missingIds = [];
  for (const id of ids) {
    const row = rowsById && rowsById[id];
    if (row) rows.push(row); else missingIds.push(id);
  }
  const har = buildHarFromRequests(rows, { title: meta.title || 'Launch evidence' });
  har.log._inspectra = {
    ...(har.log._inspectra || {}),
    evidence: { ...meta, criteria: evidence || {}, missingIds },
  };
  return { har, missingIds };
}

/**
 * sliceEvidenceHar(har, criterionId, { failedOnly }) -> { har, count, missingIds } | null
 * Narrow a saved launch evidence HAR to one criterion (or its failing rows).
 * null when the HAR carries no evidence for that criterion.
 */
export function sliceEvidenceHar(har, criterionId, { failedOnly = false } = {}) {
  const ev = har?.log?._inspectra?.evidence;
  const entry = ev?.criteria?.[criterionId];
  if (!entry) return null;
  const ids = new Set(failedOnly ? entry.failedIds || [] : entry.gradedIds || []);
  const entries = (har.log.entries || []).filter((e) => ids.has(e?._inspectra?.requestId));
  const found = new Set(entries.map((e) => e._inspectra.requestId));
  const missingIds = [...ids].filter((id) => !found.has(id));
  return {
    har: {
      ...har,
      log: {
        ...har.log,
        entries,
        _inspectra: {
          ...har.log._inspectra,
          evidence: { ...ev, criterionId, failedOnly, criteria: { [criterionId]: entry }, missingIds },
        },
      },
    },
    count: entries.length,
    missingIds,
  };
}

function slug(s, max = 40) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, max)
    .replace(/-+$/g, '');
}

/** `<ticket|adhoc>_<label|all>_<app>_<build>_<YYYY-MM-DD>[_failing].har` */
export function evidenceFilename({ ticket, label, app, build, date, failedOnly } = {}) {
  const day = (date ? new Date(date) : new Date()).toISOString().slice(0, 10);
  const parts = [
    slug(ticket, 30) || 'adhoc',
    slug(label) || 'all',
    slug(app, 30) || 'app',
    slug(build, 30) || 'nobuild',
    day,
  ];
  if (failedOnly) parts.push('failing');
  return `${parts.join('_')}.har`;
}

/** Backend HAR labels that make the evidence HAR findable from the HAR list search. */
export function evidenceLabels({ launch, build } = {}) {
  const raw = [
    launch?.id && `launch:${launch.id}`,
    launch?.ticket && `ticket:${launch.ticket}`,
    launch?.app && `app:${launch.app}`,
    build && `build:${build}`,
    `kind:${launch?.kind || 'ticket'}`,
    'evidence',
  ];
  return raw.filter(Boolean).map((l) => String(l).slice(0, LABEL_MAX));
}

/** Slim per-run snapshot posted to /proxy/launches/<id>/runs — counts, never ids. */
export function evidenceRunSnapshot({ runId, at, mode, build, evidence, harId = null, harError = null, missing = 0, statuses = {} }) {
  return {
    runId,
    at,
    mode,
    build: build || '',
    harId,
    harError,
    missing,
    criteria: Object.entries(evidence || {}).map(([id, e]) => ({
      id,
      graded: (e?.gradedIds || []).length,
      failed: (e?.failedIds || []).length,
      // The rollup's own criterion status (C16): the proxy derives the run verdict from it, so a
      // criterion whose rows were targeted but not exercised never reads as a pass.
      ...(statuses[id] ? { status: statuses[id] } : {}),
    })),
  };
}

/**
 * persistRunEvidence(...) -> { harId, missing, harError, launch, error }
 * After a RECORDED run: build the launch-level evidence HAR from the rows the
 * browser holds right now, save it to the backend (favourited, so eviction
 * skips it), and post the run snapshot. Done immediately because the proxy
 * keeps bodies for only its newest ~300 rows, so a HAR built later from ids
 * comes out partial. IO is injected (saveHar = harClient.saveHarToServer,
 * recordRun = launches.recordLaunchRun). Never throws: a failed HAR save still
 * records the run, with harError saying why.
 */
export async function persistRunEvidence({ launch, results, runId, mode, rowsById, saveHar, recordRun, now = () => new Date() }) {
  const out = { harId: null, missing: 0, harError: null, launch: null, error: null };
  try {
    if (!launch?.id || !runId) { out.error = 'no launch or run id'; return out; }
    const byCheckId = {};
    for (const r of Array.isArray(results) ? results : []) if (r?.checkId) byCheckId[r.checkId] = r;
    const evidence = criterionEvidence(launch.criteria, byCheckId);
    const at = now().toISOString();
    const build = launch.build || '';
    if (allGradedIds(evidence).length === 0) {
      out.harError = 'no graded requests';
    } else {
      const meta = { launchId: launch.id, ticket: launch.ticket || '', title: launch.title || '', app: launch.app || '', build, runId, mode, at };
      const { har, missingIds } = buildEvidenceHar({ evidence, rowsById, meta });
      out.missing = missingIds.length;
      try {
        const title = `${launch.ticket ? `${launch.ticket} · ` : ''}${launch.title || 'Launch'} · evidence`.slice(0, TITLE_MAX);
        const saved = await saveHar(har, { title, favorite: true, labels: evidenceLabels({ launch, build }) });
        out.harId = saved?.id || null;
        if (!out.harId) out.harError = 'backend returned no HAR id';
      } catch (e) {
        out.harError = String(e?.message || e || 'HAR save failed').slice(0, 200);
      }
    }
    const statuses = {};
    for (const c of Array.isArray(launch.criteria) ? launch.criteria : []) {
      if (c?.id && evidence[c.id]) statuses[c.id] = criterionRollup(c, byCheckId).status;
    }
    const snap = evidenceRunSnapshot({ runId, at, mode, build, evidence, harId: out.harId, harError: out.harError, missing: out.missing, statuses });
    const res = await recordRun(launch.id, snap);
    out.launch = res?.launch || null;
  } catch (e) {
    out.error = String(e?.message || e || 'evidence not recorded');
  }
  return out;
}

/** Trigger a browser download of a HAR object. */
export function downloadHarFile(har, filename) {
  const blob = new Blob([JSON.stringify(har, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
